/**
 * A picker's candidate lines near a line's date (TMMAPP-0174 item 3): the reimbursement manual
 * search and the transfer link step. The ± 30-day window goes to tmm-api as `from` / `to` (a
 * register has no 12-month window rule), so a line older than the newest page still finds its
 * peers; `nextCursor` is followed until `cap` lines are kept or the window is read.
 */
import { transactionsApi, type OwnerRegisterLine } from '@/api/transactions';
import { addCivilDays } from '@/shared/servedWindow';

/** Days either side of the line's date a peer may fall. */
export const NEAR_DAYS = 30;

/** The most candidates a picker shows from one account: one register page, as before. */
export const NEAR_CAP = 50;

export async function registerNear(
  accountId: number,
  postedOn: string,
  keep: (row: OwnerRegisterLine) => boolean,
): Promise<OwnerRegisterLine[]> {
  const from = addCivilDays(postedOn, -NEAR_DAYS);
  const to = addCivilDays(postedOn, NEAR_DAYS);
  const kept: OwnerRegisterLine[] = [];
  let cursor: string | undefined;
  do {
    const page = await transactionsApi.register(accountId, { from, to, cursor, limit: NEAR_CAP });
    for (const row of page.items) {
      if (!keep(row)) continue;
      kept.push(row);
      if (kept.length >= NEAR_CAP) return kept;
    }
    cursor = page.nextCursor ?? undefined;
  } while (cursor !== undefined);
  return kept;
}
